import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/i18n";
import { useQuery } from "@tanstack/react-query";
import { getVenues, getCreditPackages, buyCredits } from "@/api/endpoints";
import { Sparkles, Coins } from "lucide-react";

export function BuyCreditsDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t, lang } = useI18n();
  const [loading, setLoading] = useState(false);
  const [checkoutHtml, setCheckoutHtml] = useState("");

  const { data: venues } = useQuery({ queryKey: ["venues"], queryFn: getVenues, enabled: open });
  const { data: packages } = useQuery({ queryKey: ["credit-packages"], queryFn: getCreditPackages, enabled: open });

  const venueId = venues?.[0]?.id || (venues?.[0] as unknown as Record<string, string>)?._id;

  useEffect(() => { if (!open) setCheckoutHtml(""); }, [open]);

  const handleBuy = async (packageId: string) => {
    if (!venueId) return;
    setLoading(true);
    try {
      const res = await buyCredits({ venueId, packageId });
      if (res.checkoutFormContent) {
        setCheckoutHtml(res.checkoutFormContent);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  if (checkoutHtml) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("buy_credits_title")}</DialogTitle>
          </DialogHeader>
          <div
            className="min-h-[400px]"
            ref={(el) => {
              if (el && checkoutHtml) {
                el.innerHTML = checkoutHtml;
                const scripts = el.querySelectorAll("script");
                scripts.forEach((oldScript) => {
                  const newScript = document.createElement("script");
                  if (oldScript.src) newScript.src = oldScript.src;
                  else newScript.textContent = oldScript.textContent;
                  oldScript.parentNode?.replaceChild(newScript, oldScript);
                });
              }
            }}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md p-0 overflow-hidden">
        <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent px-6 pt-6 pb-4 text-center">
          <div className="mx-auto mb-3 flex size-16 items-center justify-center rounded-2xl bg-primary/15">
            <Coins className="size-8 text-primary" />
          </div>
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">{t("buy_credits_title")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mt-2">{t("buy_credits_desc")}</p>
        </div>

        <div className="px-6 pb-6 space-y-3">
          {!packages?.length ? (
            <p className="text-sm text-muted-foreground text-center py-4">{t("loading")}</p>
          ) : packages.map((pkg) => {
            const price = lang === "tr" ? pkg.priceTry : (pkg.priceUsd || pkg.priceTry);
            const symbol = lang === "tr" ? "₺" : "$";
            return (
              <div
                key={pkg._id}
                className="flex items-center justify-between rounded-xl border-2 p-4 hover:border-primary transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="flex size-10 items-center justify-center rounded-lg bg-primary/10">
                    <Sparkles className="size-5 text-primary" />
                  </div>
                  <p className="font-bold text-lg">{t("credit_package").replace("{{credits}}", String(pkg.credits))}</p>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleBuy(pkg._id)}
                  disabled={loading || !venueId}
                  className="font-bold"
                >
                  {symbol}{price}
                </Button>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
